import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * "SOMETHING WAS SAVED — RE-READ WHAT YOU SHOW."
 *
 * The order screen is twenty-odd panels, each with its own fetch. A save in one
 * (a VAR sheet uploaded in the card lane) never reached another (the reader it
 * should bind to), so the only cure was a full reload of a very long page.
 * Wiring every save to every reader by hand is how that happened in the first
 * place — the next panel added would miss it again.
 *
 * So this watches the WIRE, not the panels: while a screen has the watcher
 * installed, every successful non-GET fetch — any service, any helper, a
 * presigned S3 upload included — announces a change. The screen re-reads
 * quietly and bumps `DataVersionContext`; panels put that number in their
 * load effect's deps and refetch in place.
 *
 * TWO GUARDS AGAINST A REFRESH LOOP, because some READS are POSTs (ensure,
 * recheck) and a refresh that announced itself would re-trigger forever:
 *   1. READ_POSTS — the known read-shaped POSTs never announce.
 *   2. A change only counts within INPUT_WINDOW_MS of a real click or key
 *      press. A save is always the tail of something a person did; a refresh
 *      nobody asked for is not, so an unknown read-POST can loop at most for
 *      that window and then stops.
 */

const EVENT = 'rt:data-changed';
const INPUT_WINDOW_MS = 60_000;

/** POSTs that only READ (idempotent ensure/recheck) or only notify a device. */
const READ_POSTS: RegExp[] = [
  /\/admin\/applications\/ensure(\?|$)/,
  /\/admin\/journeys\/ensure(\?|$)/,
  /\/recheck-provisioning(\?|$)/,
  /\/notify(\?|$)/,
  /\/override\/request(\?|$)/,
  // Pure composes: PlanPanel and the header's monthly figure re-read these on
  // every data version, so letting them announce looped the page forever.
  /\/plan-assignments\/[^/]+\/preview(\?|$)/,
  /\/admin\/bindings\/resolve(\?|$)/,
];

let installs = 0;
let originalFetch: typeof window.fetch | null = null;
let lastInputAt = 0;
const markInput = () => { lastInputAt = Date.now(); };

const methodOf = (input: RequestInfo | URL, init?: RequestInit): string =>
  (init?.method || (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')).toUpperCase();

const urlOf = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;

export function announceDataChanged(source = 'manual'): void {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { source } }));
}

/**
 * Patch `fetch` while at least one screen wants it. Ref-counted and returns
 * its own cleanup, so `useEffect(() => installMutationWatcher(), [])` is the
 * whole integration — and leaving the screen restores the original.
 */
export function installMutationWatcher(): () => void {
  installs += 1;
  if (installs === 1) {
    originalFetch = window.fetch;
    const base = originalFetch;
    window.addEventListener('pointerdown', markInput, true);
    window.addEventListener('keydown', markInput, true);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const res = await base(input as any, init);
      try {
        const method = methodOf(input, init);
        if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS' && res.ok) {
          const url = urlOf(input);
          const recentInput = Date.now() - lastInputAt < INPUT_WINDOW_MS;
          if (recentInput && !READ_POSTS.some((re) => re.test(url))) announceDataChanged(`${method} ${url}`);
        }
      } catch { /* watching must never break the request it watched */ }
      return res;
    };
  }
  return () => {
    installs -= 1;
    if (installs === 0 && originalFetch) {
      window.fetch = originalFetch;
      originalFetch = null;
      window.removeEventListener('pointerdown', markInput, true);
      window.removeEventListener('keydown', markInput, true);
    }
  };
}

/**
 * Run `cb` once after a burst of changes settles. A save is usually several
 * requests (presign → PUT → confirm), and one quiet re-read after the last is
 * what the screen wants, not three.
 */
export function useDataChanged(cb: () => void, settleMs = 450): void {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    let timer: number | undefined;
    const on = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => ref.current(), settleMs);
    };
    window.addEventListener(EVENT, on);
    return () => { window.removeEventListener(EVENT, on); window.clearTimeout(timer); };
  }, [settleMs]);
}

/**
 * Bumped by a screen after its quiet re-read. A panel that loads its own data
 * reads it with `useDataVersion()` and lists it in that load's deps. 0 outside
 * any provider, so a panel used elsewhere behaves exactly as before.
 */
export const DataVersionContext = createContext(0);
export const useDataVersion = () => useContext(DataVersionContext);
